/**
 * Analytics Event Catalog
 *
 * Single source of truth for all tracked events and their property shapes.
 * Add new events here first, then add the corresponding entry to
 * backend/services/analytics/events.py and docs/Operations/Analytics/ANALYTICS_SYSTEM.md.
 *
 * Rule: property names are snake_case. User-level traits that never change per
 * session (plan, org_id, etc.) belong on identify(), not on individual events.
 *
 * Server-only events (rollup, Stripe webhooks, etc.) are declared in
 * backend/services/analytics/events.py::SERVER_ONLY_EVENTS and do NOT appear
 * in this file. The TestEventCatalog parity test enforces this split.
 */

// ---------------------------------------------------------------------------
// Lifecycle
// ---------------------------------------------------------------------------
export interface SignupStartedProps {
  source: string;
  funnel_id?: string;
  persona?: string;
  page_slug?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
}

export interface SignupCompletedProps {
  method: string;
  invited: boolean;
  referrer_user_id?: string;
}

export interface UserIdentifiedProps {
  user_id: string;
}

export interface LoginProps {
  method: 'email' | 'google_oauth' | 'auth0' | 'email_password' | string;
  user_id?: string;
}

// ---------------------------------------------------------------------------
// Session
// ---------------------------------------------------------------------------
export interface SessionStartedProps {
  session_id: string;
}

export interface SessionEndedProps {
  session_id: string;
  duration_ms: number;
  end_reason: 'pagehide' | 'idle' | 'logout' | 'reset';
  events_in_session: number;
  agent_runs_in_session: number;
  messages_sent_in_session: number;
}

// ---------------------------------------------------------------------------
// Onboarding — vocabulary A (product /onboarding route)
// ---------------------------------------------------------------------------
export type OnboardingStep = 'welcome' | 'auth' | 'integrations' | 'analysis' | 'profile' | 'launch';

export interface OnboardingStartedProps {
  funnel_id?: string;
  persona?: string;
}

export interface OnboardingStepCompletedProps {
  step: OnboardingStep;
  step_index: number;
  step_duration_ms: number;
  total_elapsed_ms: number;
}

export interface OnboardingSkippedProps {
  step: OnboardingStep;
  reason?: string;
}

export interface OnboardingCompletedProps {
  total_duration_ms: number;
  steps_skipped: number;
}

// ---------------------------------------------------------------------------
// Onboarding — vocabulary C (general onboarding wizard, wizard mode)
// ---------------------------------------------------------------------------
export interface GeneralOnboardingStartedProps {
  mode: 'wizard' | 'inchat' | 'legacy';
  wizard_variant?: string;
  funnel_id?: string;
}

export interface GeneralOnboardingStepCompletedProps {
  stepId: string;
  stepIndex: number;
  durationMs: number;
  mode: string;
  /** 'entered' on arrival, 'completed' on Continue, 'skipped' on Skip, 'back_navigated' on Back */
  action: 'entered' | 'completed' | 'skipped' | 'back_navigated';
  wizard_variant?: string;
  funnel_id?: string;
}

export interface GeneralOnboardingFlowCompletedProps {
  mode: string;
  totalDurationMs: number;
  stepsCompleted: string[];
  skippedSteps: string[];
  hasPersona: boolean;
  role?: string;
  connectedCount: number;
  integrations_skipped: boolean;
  /** @deprecated The "choose your starting point" step was removed — every wizard run is now blank_space. */
  hasProject?: boolean;
  /** @deprecated The "choose your starting point" step was removed — every wizard run is now blank_space. */
  projectKind?: string;
  wizard_variant?: string;
  funnel_id?: string;
  connected_integration_ids?: string[];
  space_choice?: string;
}

export interface GeneralOnboardingPersonaSelectedProps {
  persona: string;
  wizard_variant?: string;
}

export interface GeneralOnboardingAiGoalsGeneratedProps {
  persona: string;
  role?: string | null;
  new_chip_count: number;
  total_chips: number;
  duration_ms: number;
  success: boolean;
  wizard_variant?: string;
}

export interface GeneralOnboardingIntegrationConnectAttemptedProps {
  integration_name: string;
  persona?: string | null;
  was_suggested: boolean;
  wizard_variant?: string;
}

export interface GeneralOnboardingIntegrationConnectSucceededProps {
  integration_name: string;
  persona?: string | null;
  was_suggested: boolean;
  duration_ms: number;
  wizard_variant?: string;
}

export interface GeneralOnboardingIntegrationConnectFailedProps {
  integration_name: string;
  error_type: string;
  wizard_variant?: string;
}

export interface GeneralOnboardingAbandonedProps {
  last_step_id: string;
  last_step_index: number;
  total_duration_ms: number;
  steps_completed: string[];
  steps_skipped: string[];
  wizard_variant?: string;
}

// Earn AI Credits events
export interface EarnAiCreditsViewedProps {
  [key: string]: never; // no required props
}

export interface EarnAiCreditCtaClickedProps {
  reward_type: 'integration_connect' | 'referral_invite';
  action?: string;
}

export interface AiCreditRewardClaimedProps {
  reward_type: string;
  amount_usd: number;
  url?: string;
}

export interface AiCreditRewardClaimFailedProps {
  reward_type: string;
  error?: string;
}

export interface OnboardingWidgetIntegrationChipClickedProps {
  slug: string;
}

// ---------------------------------------------------------------------------
// Onboarding — vocabulary B (marketing /onboardingFlows)
// These replace the old per-flow ad-hoc events (seo_optimizer_onboarding_viewed, etc.)
// ---------------------------------------------------------------------------
export interface FlowStepViewedProps {
  flow_id: string;
  step: string;
  step_index?: number;
  funnel_id?: string;
  page_slug?: string;
}

export interface FlowStepCompletedProps {
  flow_id: string;
  step: string;
  step_index?: number;
  step_duration_ms?: number;
  funnel_id?: string;
}

export interface FlowStepFailedProps {
  flow_id: string;
  step: string;
  reason?: string;
}

export interface FlowInteractionProps {
  flow_id: string;
  interaction_type: string;
  step?: string;
  funnel_id?: string;
  value?: string;
}

// ---------------------------------------------------------------------------
// Marketing / landing
// ---------------------------------------------------------------------------
export interface MarketingCtaClickedProps {
  cta_slug: string;
  page_slug?: string;
  page_group?: string;
  funnel_id?: string;
}

// ---------------------------------------------------------------------------
// Grant Workspace Builder (NIH grants wizard at /build/grant-workspace)
// ---------------------------------------------------------------------------
export interface GrantBuilderMechanismChosenProps {
  category: string;
  mechanism: string;
  submission_type?: string;
  funnel_id: string;
  source: 'category_hero_cta' | 'category_bytype_cta' | string;
}

export interface GrantBuilderStepCompletedProps {
  step: number;
  mechanism: string;
  funnel_id: string;
}

export interface GrantBuilderDraftSavedProps {
  step: number;
  mechanism: string;
  funnel_id: string;
}

export interface GrantBuilderSubmittedProps {
  mechanism: string;
  funnel_id: string;
  flag_count: number;
  has_aims: boolean;
  has_pi_name: boolean;
}

export interface GrantBuilderSkippedProps {
  step: number;
  mechanism: string;
  funnel_id: string;
  flag_count: number;
}

export interface HeroInputSubmittedProps {
  page_slug?: string;
  page_group?: string;
  funnel_id?: string;
  input_length?: number;
}

export interface WorkspaceCreatedProps {
  source?: string;
  funnel_id?: string;
  page_slug?: string;
}

export interface WorkspaceFirstViewProps {
  source?: string;
  onboarding_source?: string;
  space_id: string;
}

export interface LandingContextRestoredProps {
  funnel_id?: string;
  space_id?: string;
}

export interface OperatorPersonaStartedProps {
  funnel_id: string;
  persona?: string;
}

export interface DeploymentToggleProps {
  mode: 'cloud' | 'device';
  page_slug?: string;
}

export interface AgeVerificationShownProps {
  funnel_id?: string;
}

export interface AgeVerificationConfirmedProps {
  funnel_id?: string;
}

export interface AgeVerificationDeclinedProps {
  funnel_id?: string;
}

export interface ContactFormSubmittedProps {
  page_slug?: string;
  subject?: string;
}

export interface ErrorPageViewedProps {
  error_code?: string;
  path?: string;
}

// ---------------------------------------------------------------------------
// QuickLauncher
// ---------------------------------------------------------------------------
export interface QuickLauncherOpenedProps {
  source?: string;
}

export interface QuickLauncherClosedProps {
  duration_ms?: number;
}

export interface QuickLauncherFilesDroppedProps {
  file_count: number;
}

export interface QuickLauncherMessageSentProps {
  message_length: number;
}

export interface QuickLauncherDocumentCreatedProps {
  doc_type?: string;
}

export interface QuickLauncherAppLaunchedProps {
  app_name: string;
}

// ---------------------------------------------------------------------------
// App-level
// ---------------------------------------------------------------------------
export interface AppOpenedProps {
  app_name: string;
  source?: string;
}

export interface DocumentClickedProps {
  doc_id: string;
  source?: string;
}

export interface DocumentSharedProps {
  doc_id: string;
  share_type?: string;
}

// ---------------------------------------------------------------------------
// Engagement / chat
// ---------------------------------------------------------------------------
export interface FirstUserMessageProps {
  space_id: string;
  message_length?: number;
  time_since_signup_ms?: number;
  funnel_id?: string;
  page_group?: string;
  page_slug?: string;
}

export interface ChatMessageSentProps {
  space_id: string;
  thread_id?: string;
  message_length: number;
  has_attachment: boolean;
  used_web_search: boolean;
  message_number: number;
  // Surfaces / variants of the chat input
  surface?: 'chat' | 'hero' | 'quicklauncher';
  // AI model id selected when sending
  model?: string;
  // Whether Max Mode (1M token context) was active
  max_mode?: boolean;
  // Whether an image was attached to this message
  has_image?: boolean;
  // Number of @-mention chiplets in the message
  mention_count?: number;
  // Number of context items attached (entity refs + entity contexts + file uploads + websites + explicit text)
  context_count?: number;
  // Number of entities selected from the inline MentionSuggestions popup (typed @ in the editor)
  inline_mention_count?: number;
  // Slash command used to dispatch this message (e.g. "triage"); undefined for normal messages
  slash_command?: string;
  // True when the message text starts with a `/` slash trigger
  via_slash_command?: boolean;
  // First message in this thread vs continuation
  is_first_in_thread?: boolean;
}

export interface ChatPersonaChangedProps {
  from: 'general' | 'creative' | 'gtm';
  to: 'general' | 'creative' | 'gtm';
}

export interface NpsResponseSubmittedProps {
  score: number;
  segment?: string;
  surface?: string;
}

export interface FirstRunFeedbackSubmittedProps {
  sentiment: 'positive' | 'negative';
  run_id: string;
}

// ---------------------------------------------------------------------------
// First-value / activation
// ---------------------------------------------------------------------------
export interface FirstAgentRunStartedProps {
  space_id: string;
  run_id: string;
}

export interface FirstAgentRunCompletedProps {
  space_id: string;
  run_id: string;
  duration_ms: number;
  ttv_seconds: number;
}

export interface SecondAgentRunCompletedProps {
  space_id: string;
  run_id: string;
  ttsr_seconds?: number;
}

export interface ThirdAgentRunCompletedProps {
  space_id: string;
  run_id: string;
  time_to_third_value_seconds?: number;
}

export interface FirstFileUploadedProps {
  file_type: string;
  space_id?: string;
}

export interface FirstIntegrationConnectedProps {
  integration_name: string;
  integration_category: string;
}

export interface FirstAutomationCreatedProps {
  automation_id: string;
  space_id?: string;
}

// ---------------------------------------------------------------------------
// Tool usage
// ---------------------------------------------------------------------------
export interface ToolInvokedProps {
  tool_name: string;
  tool_category: string;
  integration_name?: string;
  space_id?: string;
  run_id?: string;
  correlation_id?: string;
}

export interface ToolCompletedProps {
  tool_name: string;
  tool_category: string;
  integration_name?: string;
  space_id?: string;
  run_id?: string;
  correlation_id?: string;
  success: boolean;
  duration_ms: number;
  error_type?: string;
}

// ---------------------------------------------------------------------------
// Integration lifecycle
// ---------------------------------------------------------------------------
export interface IntegrationConnectedProps {
  integration_name: string;
  integration_category: string;
  auth_method: string;
  is_first_integration: boolean;
  is_reconnect: boolean;
  org_id?: string;
}

export interface IntegrationDisconnectedProps {
  integration_name: string;
  integration_category: string;
  integrations_remaining: number;
}

export interface IntegrationAuthFailedProps {
  integration_name: string;
  error_type: string;
}

// ---------------------------------------------------------------------------
// Collaboration
// ---------------------------------------------------------------------------
export interface InviteSentProps {
  org_id?: string;
  entity_id?: string;
  invite_type: 'org' | 'entity';
  invite_count?: number;
}

export interface InviteAcceptedProps {
  org_id?: string;
  invite_type: 'org' | 'entity';
  time_to_accept_ms?: number;
}

export interface FileUploadedProps {
  file_type: string;
  space_id?: string;
}

// ---------------------------------------------------------------------------
// Retention (client-visible subset — rollup events are server-only)
// ---------------------------------------------------------------------------
export interface Day2ReturnedProps {
  days_since_signup: number;
}

export interface Day7ReturnedProps {
  days_since_signup: number;
}

export interface WeeklyActiveProps {
  week_number: number;
}

// ---------------------------------------------------------------------------
// Monetization — upgrade flow (client + server)
// ---------------------------------------------------------------------------
export interface UpgradeCtaViewedProps {
  source: string;
  feature_gate?: string;
  plan_offered?: string;
}

export interface UpgradeCtaClickedProps {
  source: string;
  feature_gate?: string;
  plan_offered?: string;
}

export interface CheckoutStartedProps {
  source: string;
  plan?: string;
  seats?: number;
  is_payg: boolean;
}

export interface CheckoutAbandonedProps {
  source: string;
  plan?: string;
  reason?: 'modal_closed' | 'timeout';
}

export interface SubscriptionStartedProps {
  plan: string;
  seats?: number;
  mrr?: number;
  stripe_event_id: string;
  days_since_signup?: number;
}

export interface SubscriptionChangedProps {
  plan_from: string;
  plan_to: string;
  seats_from?: number;
  seats_to?: number;
  mrr_from?: number;
  mrr_to?: number;
  stripe_event_id: string;
}

export interface SubscriptionCanceledProps {
  plan: string;
  stripe_event_id: string;
}

export interface PaymentFailedProps {
  plan: string;
  amount_due?: number;
  stripe_event_id: string;
}

export interface AiCreditsPurchasedProps {
  amount_usd: number;
  credits: number;
  stripe_event_id?: string;
  org_id?: string;
}

// ---------------------------------------------------------------------------
// Support / errors
// ---------------------------------------------------------------------------
export interface SupportWidgetOpenedProps {
  source?: string;
}

export interface ErrorShownProps {
  error_type: string;
  message?: string;
  source?: string;
}

// ---------------------------------------------------------------------------
// Store funnel (client-side events)
// ---------------------------------------------------------------------------
export interface StoreListingViewedProps {
  store_listing_id: string;
  source: 'store_home' | 'search' | 'deep_link';
}

// ---------------------------------------------------------------------------
// Typed map: event name → props type
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Broadcast popups (dedicated in-app popups, gui/src/popups/)
// ---------------------------------------------------------------------------
export interface PopupShownProps {
  popup_id: string;
  popup_seq: number;
}
export interface PopupDismissedProps {
  popup_id: string;
  popup_seq: number;
}
export interface PopupFeedbackSubmittedProps {
  popup_id: string;
  rating: number;
  has_text: boolean;
}

// ---------------------------------------------------------------------------
// Transcripts
// ---------------------------------------------------------------------------
export interface TranscriptOpenedProps {
  transcriptId: string;
  source?: string;
}

export interface TranscriptEntityClickedProps {
  entityId: string;
  entityType?: string;
}

export interface TranscriptSegmentClickedProps {
  transcriptId: string;
  startMs: number;
}

export interface TranscriptDemoSeededProps {
  [key: string]: unknown;
}

export interface MeetingRecordingStartedProps {
  transcriptId: string;
  calendarEventId?: string;
}

export interface MeetingRecordingCompletedProps {
  transcriptId: string;
  durationMs?: number;
  segmentCount?: number;
}

export interface MeetingSummaryViewedProps {
  transcriptId: string;
}

export interface MeetingActionItemsCreatedProps {
  transcriptId: string;
  count: number;
}

export interface CalendarConnectedProps {
  provider: string;
}

// ---------------------------------------------------------------------------
// Home redesign
// ---------------------------------------------------------------------------
export interface HomeInitialChatSubmittedProps {
  prompt_length: number;
  has_image: boolean;
  space_id?: string;
}
export interface HomeRecentThreadOpenedProps {
  thread_id: string;
  has_space: boolean;
  is_headless: boolean;
}
export interface HomeQuickStartClickedProps {
  action: string;
}
export interface HomeDockAppOpenProps {
  appId: string;
}
export interface HomeRootCreateProps {
  entity_type: string;
  root_space_id: string;
}
export interface HomeRootDockOpenProps {
  app_id: string;
  root_space_id: string;
}
export interface HomeAppFocusOpenProps {
  app_id: string;
  view: string | null;
  root_space_id: string;
}
export interface HomeWidgetAddedProps {
  widget_type: string;
}
export interface HomeWidgetLayoutSavedProps {
  widget_count: number;
}

// ---------------------------------------------------------------------------
// DashboardApp
// ---------------------------------------------------------------------------
export interface DashboardOpenedProps {
  section_count: number;
  schema_version: string;
}
export interface DashboardWidgetAddedProps {
  kind: string;
  viz?: string;
  refresh_mode?: string;
}
export interface DashboardWidgetRefreshedProps {
  kind?: string;
  section_type?: string;
}
export interface DashboardWidgetErroredProps {
  kind?: string;
  section_type?: string;
  error?: string;
}
export interface DashboardWidgetRemovedProps {
  section_id?: string;
}

// ---------------------------------------------------------------------------
// Spend-limit consent (PAYG explicit approval flow)
// ---------------------------------------------------------------------------
export interface SpendLimitPromptShownProps {
  trigger_reason: 'gate_trip' | 'manual_settings';
  presented_options_cents: number[];
  current_limit_cents: number;
  is_blocking: boolean;
  plan: string;
  thread_id?: string;
}

export interface SpendLimitApprovedProps {
  chosen_cents: number;
  presented_cents: number[];
  trigger_reason: 'gate_trip' | 'manual_settings';
  plan: string;
  previous_limit_cents: number;
  new_limit_cents: number;
}

export interface SpendLimitDismissedProps {
  trigger_reason: 'gate_trip' | 'manual_settings';
  plan: string;
  current_limit_cents: number;
}

export interface SpendLimitCapRaisedManualProps {
  chosen_cents: number;
  previous_limit_cents: number;
  plan: string;
}

// Uncapped (unlimited) spending authorization — strongest form of consent.
// Triggered when an org owner explicitly agrees to be billed monthly with
// no upper limit. Three events bracket the disclosure flow:
//   prompt_shown — confirm modal opened (no authorization yet)
//   authorized   — user clicked through, server recorded the verbatim
//                  disclosure + policy version on an audit row
//   revoked      — user reverted to capped mode from settings

export interface SpendLimitUncappedPromptShownProps {
  trigger_reason: 'gate_trip' | 'manual_settings';
  plan: string;
  current_limit_cents: number;
}

export interface SpendLimitUncappedAuthorizedProps {
  trigger_reason: 'gate_trip' | 'manual_settings';
  plan: string;
  preserved_cap_cents: number;
  policy_version?: string;
}

export interface SpendLimitUncappedRevokedProps {
  trigger_reason: 'gate_trip' | 'manual_settings';
  plan: string;
  restored_cap_cents: number;
}

// ---------------------------------------------------------------------------
// Domain rollout (riverworkspace.com migration)
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// WS health beacon (Rock-Solid WebSocket Recovery) — Sentry-independent,
// CloudWatch-queryable signal for Y.js WebSocket wedge conditions. Sentry has
// been observed to have zero events for affected populations (the
// chandler+marketing incident had no client-side exception at all — just a
// silent real-time sync gap), so this rides the existing analytics pipe
// instead of relying solely on Sentry.
// ---------------------------------------------------------------------------
export interface WsHealthIssueProps {
  issue_type: 'never_synced' | 'cumulative_downtime' | 'hard_stop' | 'zombie_recovery' | 'size_budget_verification';
  /** Room name prefix, e.g. 'org', 'space', 'doc' — never the full room name (may contain IDs). */
  room_prefix: string;
  /** Resolved WEBSOCKET_URL at the time of the issue — catches stale localStorage overrides. */
  websocket_url: string;
  /** Elapsed/cumulative not-synced time in ms. Present for never_synced and cumulative_downtime. */
  downtime_ms?: number;
  /** Which wedge condition triggered a zombie_recovery force-reconnect. */
  wedge_kind?: 'connected_never_synced' | 'stuck_connecting';
  /** WS close code that triggered a hard_stop, if any. */
  close_code?: number;
  /** WS close reason / hard-stop cause. */
  close_reason?: string;
  /**
   * Outcome of wsSizeBudget's off-thread re-verification after a client-side
   * size-budget trip. Present for issue_type='size_budget_verification'.
   */
  verification_outcome?: 'false_positive_cleared' | 'confirmed_oversized' | 'verification_failed';
  /** The monotonic update-log byte counter that triggered the trip. */
  accumulator_bytes?: number;
  /** The doc's actual off-thread encoded state size, once verified. Absent if the encode failed. */
  real_bytes?: number;
}

// ---------------------------------------------------------------------------
// Real User Monitoring — Core Web Vitals (LCP/CLS/INP/FCP/TTFB) captured from
// actual production traffic via the `web-vitals` library. See
// gui/src/reportWebVitals.ts, wired in from gui/src/index.tsx.
// ---------------------------------------------------------------------------
export interface WebVitalRecordedProps {
  metric_name: 'CLS' | 'INP' | 'FCP' | 'LCP' | 'TTFB';
  /** Raw metric value in the library's native unit (ms, except CLS which is unitless). */
  value: number;
  /** web-vitals' own good/needs-improvement/poor bucket for this value. */
  rating: 'good' | 'needs-improvement' | 'poor';
  /** Path the metric was recorded on, e.g. window.location.pathname. */
  path: string;
}

export interface DomainDetectedProps {
  hostname: string;
  is_canonical: boolean;
  is_legacy: boolean;
  is_production: boolean;
  is_selfhosted: boolean;
  is_dev: boolean;
  api_origin: string;
  cookie_domain?: string;
}


export interface EventMap {
  // Lifecycle
  signup_started: SignupStartedProps;
  signup_completed: SignupCompletedProps;
  login: LoginProps;
  // Session
  session_started: SessionStartedProps;
  session_ended: SessionEndedProps;
  // Onboarding A (product onboarding route)
  onboarding_started: OnboardingStartedProps;
  onboarding_step_completed: OnboardingStepCompletedProps;
  onboarding_completed: OnboardingCompletedProps;
  // Onboarding C (general wizard — live events only; dead events removed pending redesign)
  general_onboarding_started: GeneralOnboardingStartedProps;
  general_onboarding_step_completed: GeneralOnboardingStepCompletedProps;
  general_onboarding_flow_completed: GeneralOnboardingFlowCompletedProps;
  general_onboarding_persona_selected: GeneralOnboardingPersonaSelectedProps;
  general_onboarding_integration_connect_attempted: GeneralOnboardingIntegrationConnectAttemptedProps;
  general_onboarding_integration_connect_succeeded: GeneralOnboardingIntegrationConnectSucceededProps;
  general_onboarding_integration_connect_failed: GeneralOnboardingIntegrationConnectFailedProps;
  general_onboarding_abandoned: GeneralOnboardingAbandonedProps;
  // Earn AI Credits
  earn_ai_credits_viewed: EarnAiCreditsViewedProps;
  earn_ai_credits_cta_clicked: EarnAiCreditCtaClickedProps;
  ai_credit_reward_claimed: AiCreditRewardClaimedProps;
  ai_credit_reward_claim_failed: AiCreditRewardClaimFailedProps;
  // Onboarding widget (persistent bottom-left checklist)
  onboarding_widget_viewed: Record<string, never>;
  onboarding_widget_integration_chip_clicked: OnboardingWidgetIntegrationChipClickedProps;
  onboarding_widget_profile_clicked: Record<string, never>;
  onboarding_widget_project_clicked: Record<string, never>;
  // Onboarding B (marketing flows)
  flow_step_viewed: FlowStepViewedProps;
  flow_step_completed: FlowStepCompletedProps;
  flow_step_failed: FlowStepFailedProps;
  flow_interaction: FlowInteractionProps;
  // Marketing / landing
  marketing_cta_clicked: MarketingCtaClickedProps;
  hero_input_submitted: HeroInputSubmittedProps;
  // Grant Workspace Builder
  grant_builder_mechanism_chosen: GrantBuilderMechanismChosenProps;
  grant_builder_step_completed: GrantBuilderStepCompletedProps;
  grant_builder_draft_saved: GrantBuilderDraftSavedProps;
  grant_builder_submitted: GrantBuilderSubmittedProps;
  grant_builder_skipped: GrantBuilderSkippedProps;
  workspace_created: WorkspaceCreatedProps;
  workspace_first_view: WorkspaceFirstViewProps;
  landing_context_restored: LandingContextRestoredProps;
  deployment_toggle: DeploymentToggleProps;
  age_verification_shown: AgeVerificationShownProps;
  age_verification_confirmed: AgeVerificationConfirmedProps;
  age_verification_declined: AgeVerificationDeclinedProps;
  contact_form_submitted: ContactFormSubmittedProps;
  error_page_viewed: ErrorPageViewedProps;
  // QuickLauncher
  quick_launcher_opened: QuickLauncherOpenedProps;
  quick_launcher_closed: QuickLauncherClosedProps;
  quick_launcher_files_dropped: QuickLauncherFilesDroppedProps;
  quick_launcher_message_sent: QuickLauncherMessageSentProps;
  quick_launcher_document_created: QuickLauncherDocumentCreatedProps;
  quick_launcher_app_launched: QuickLauncherAppLaunchedProps;
  // App-level
  app_opened: AppOpenedProps;
  document_clicked: DocumentClickedProps;
  document_shared: DocumentSharedProps;
  // Engagement / chat
  first_user_message: FirstUserMessageProps;
  chat_message_sent: ChatMessageSentProps;
  chat_persona_changed: ChatPersonaChangedProps;
  // First-value / activation
  first_agent_run_started: FirstAgentRunStartedProps;
  first_agent_run_completed: FirstAgentRunCompletedProps;
  second_agent_run_completed: SecondAgentRunCompletedProps;
  third_agent_run_completed: ThirdAgentRunCompletedProps;
  first_file_uploaded: FirstFileUploadedProps;
  first_integration_connected: FirstIntegrationConnectedProps;
  first_automation_created: FirstAutomationCreatedProps;
  // Tool usage (direct proxy calls)
  tool_invoked: ToolInvokedProps;
  tool_completed: ToolCompletedProps;
  // Integration lifecycle
  integration_connected: IntegrationConnectedProps;
  integration_disconnected: IntegrationDisconnectedProps;
  // Collaboration
  invite_sent: InviteSentProps;
  invite_accepted: InviteAcceptedProps;
  file_uploaded: FileUploadedProps;
  // Retention (client-visible)
  day2_returned: Day2ReturnedProps;
  day7_returned: Day7ReturnedProps;
  weekly_active: WeeklyActiveProps;
  // Spend-limit consent
  spend_limit_prompt_shown: SpendLimitPromptShownProps;
  spend_limit_approved: SpendLimitApprovedProps;
  spend_limit_dismissed: SpendLimitDismissedProps;
  spend_limit_cap_raised_manual: SpendLimitCapRaisedManualProps;
  spend_limit_uncapped_prompt_shown: SpendLimitUncappedPromptShownProps;
  spend_limit_uncapped_authorized: SpendLimitUncappedAuthorizedProps;
  spend_limit_uncapped_revoked: SpendLimitUncappedRevokedProps;
  // Monetization
  upgrade_cta_viewed: UpgradeCtaViewedProps;
  upgrade_cta_clicked: UpgradeCtaClickedProps;
  checkout_started: CheckoutStartedProps;
  checkout_abandoned: CheckoutAbandonedProps;
  subscription_started: SubscriptionStartedProps;
  subscription_changed: SubscriptionChangedProps;
  subscription_canceled: SubscriptionCanceledProps;
  payment_failed: PaymentFailedProps;
  ai_credits_purchased: AiCreditsPurchasedProps;
  // Errors
  error_shown: ErrorShownProps;
  // Store funnel
  store_listing_viewed: StoreListingViewedProps;
  // Broadcast popups (dedicated in-app popups)
  popup_shown: PopupShownProps;
  popup_dismissed: PopupDismissedProps;
  popup_feedback_submitted: PopupFeedbackSubmittedProps;
  // Transcripts
  transcript_opened: TranscriptOpenedProps;
  transcript_entity_clicked: TranscriptEntityClickedProps;
  transcript_demo_seeded: TranscriptDemoSeededProps;
  meeting_recording_started: MeetingRecordingStartedProps;
  meeting_recording_completed: MeetingRecordingCompletedProps;
  meeting_summary_viewed: MeetingSummaryViewedProps;
  meeting_action_items_created: MeetingActionItemsCreatedProps;
  calendar_connected: CalendarConnectedProps;
  // Home redesign
  home_initial_chat_submitted: HomeInitialChatSubmittedProps;
  home_recent_thread_opened: HomeRecentThreadOpenedProps;
  home_quick_start_clicked: HomeQuickStartClickedProps;
  home_dock_app_open: HomeDockAppOpenProps;
  home_root_create: HomeRootCreateProps;
  home_root_dock_open: HomeRootDockOpenProps;
  home_app_focus_open: HomeAppFocusOpenProps;
  home_widget_added: HomeWidgetAddedProps;
  home_widget_layout_saved: HomeWidgetLayoutSavedProps;
  // DashboardApp
  dashboard_opened: DashboardOpenedProps;
  dashboard_widget_added: DashboardWidgetAddedProps;
  dashboard_widget_refreshed: DashboardWidgetRefreshedProps;
  dashboard_widget_errored: DashboardWidgetErroredProps;
  dashboard_widget_removed: DashboardWidgetRemovedProps;
  // Database connections
  database_connection_created: DatabaseConnectionCreatedProps;
  database_connection_tested: DatabaseConnectionTestedProps;
  database_query_run: DatabaseQueryRunProps;
  database_provider_selected: DatabaseProviderSelectedProps;
  database_provider_request_submitted: DatabaseProviderRequestSubmittedProps;
  // Browser credential fill (active events only; unimplemented profile events removed)
  browser_credential_prompt_shown: BrowserCredentialPromptShownProps;
  browser_credential_filled: BrowserCredentialFilledProps;
  browser_credential_saved: BrowserCredentialSavedProps;
  browser_credential_dismissed: BrowserCredentialDismissedProps;
  browser_card_filled: BrowserCardFilledProps;
  browser_identity_filled: BrowserIdentityFilledProps;
  browser_totp_filled: BrowserTotpFilledProps;
  browser_profile_revoked_all: BrowserProfileRevokedAllProps;
  // MediaGen
  mediagen_modal_opened: MediagenModalOpenedProps;
  mediagen_template_selected: MediagenTemplateSelectedProps;
  mediagen_generation_completed: MediagenGenerationCompletedProps;
  // Performance — space sidebar / chat first paint
  space_sidebar_loaded_ms: SpaceSidebarLoadedMsProps;
  space_chat_first_paint_ms: SpaceChatFirstPaintMsProps;
  // Integration pill (chat input smart-suggest)
  integration_pill_shown: IntegrationPillShownProps;
  integration_pill_clicked: IntegrationPillClickedProps;
  integration_pill_added: IntegrationPillAddedProps;
  integration_pill_details_clicked: IntegrationPillDetailsClickedProps;
  integration_pill_try_in_chat_clicked: IntegrationPillTryInChatClickedProps;
  // Service Creator (CreateMenu "Create from…" section)
  service_creator_menu_built: ServiceCreatorMenuBuiltProps;
  service_creator_invoked: ServiceCreatorInvokedProps;
  service_creator_completed: ServiceCreatorCompletedProps;
  service_creator_failed: ServiceCreatorFailedProps;
  service_creator_connect_clicked: ServiceCreatorConnectClickedProps;
  // Tool TOC flow (active events only)
  tool_analysis_completed: ToolAnalysisCompletedProps;
  tool_chip_toggled: ToolChipToggledProps;
  tool_sticky_action_clicked: ToolStickyActionClickedProps;
  // Physician tool intake (active events only)
  tool_specialty_selected: ToolSpecialtySelectedProps;
  // Marketing mini-tools
  mini_tool_viewed: MiniToolViewedProps;
  mini_tool_submitted: MiniToolSubmittedProps;
  // Community hub events (live in content-renderer)
  community_category_tab_clicked: CommunityCategoryTabClickedProps;
  doc_template_config_submitted: DocTemplateConfigSubmittedProps;
  doc_template_logo_generate_selected: DocTemplateLogoGenerateSelectedProps;
  // Data Source events (active events only)
  data_source_installed: { source_id: string; source_name: string; consumption_mode: string; entry_count: number };
  data_source_searched: { source_id: string; query: string; hit_count: number };
  data_source_attached: { source_id: string; source_name: string; consumption_mode: string };
  // Physician tools (active events only)
  physician_tool_submitted: {
    slug: string;
    specialty: string | null;
    has_config: boolean;
    requires_card: boolean;
    preload_tools: string[];
  };
  // Physician widgets
  physician_widget_opened: {
    widget_id: string;
    source: 'launcher' | 'agent' | 'provision';
  };
  physician_widget_computed: {
    widget_id: string;
  };
  // WorkflowApp / Setup Wizard (wizard_started live; step/complete deferred until redesign)
  wizard_started: {
    wizard_id: string;
    source: 'drive_button' | 'agent_tool' | 'direct';
  };
  // WS health beacon (Rock-Solid WebSocket Recovery)
  ws_health_issue: WsHealthIssueProps;
  // Real User Monitoring — Core Web Vitals from actual production traffic
  web_vital_recorded: WebVitalRecordedProps;
}

export type EventName = keyof EventMap;
export type EventProps<E extends EventName> = EventMap[E];

/** Set of valid event names for runtime validation. Must match EventMap keys. */
export const VALID_EVENTS = new Set<string>(Object.keys({
  signup_started: 1,
  signup_completed: 1,
  login: 1,
  session_started: 1,
  session_ended: 1,
  onboarding_started: 1,
  onboarding_step_completed: 1,
  onboarding_completed: 1,
  general_onboarding_started: 1,
  general_onboarding_step_completed: 1,
  general_onboarding_flow_completed: 1,
  general_onboarding_persona_selected: 1,
  general_onboarding_integration_connect_attempted: 1,
  general_onboarding_integration_connect_succeeded: 1,
  general_onboarding_integration_connect_failed: 1,
  general_onboarding_abandoned: 1,
  earn_ai_credits_viewed: 1,
  earn_ai_credits_cta_clicked: 1,
  ai_credit_reward_claimed: 1,
  ai_credit_reward_claim_failed: 1,
  onboarding_widget_viewed: 1,
  onboarding_widget_integration_chip_clicked: 1,
  onboarding_widget_profile_clicked: 1,
  onboarding_widget_project_clicked: 1,
  flow_step_viewed: 1,
  flow_step_completed: 1,
  flow_step_failed: 1,
  flow_interaction: 1,
  marketing_cta_clicked: 1,
  hero_input_submitted: 1,
  grant_builder_mechanism_chosen: 1,
  grant_builder_step_completed: 1,
  grant_builder_draft_saved: 1,
  grant_builder_submitted: 1,
  grant_builder_skipped: 1,
  workspace_created: 1,
  workspace_first_view: 1,
  landing_context_restored: 1,
  deployment_toggle: 1,
  age_verification_shown: 1,
  age_verification_confirmed: 1,
  age_verification_declined: 1,
  contact_form_submitted: 1,
  error_page_viewed: 1,
  quick_launcher_opened: 1,
  quick_launcher_closed: 1,
  quick_launcher_files_dropped: 1,
  quick_launcher_message_sent: 1,
  quick_launcher_document_created: 1,
  quick_launcher_app_launched: 1,
  app_opened: 1,
  document_clicked: 1,
  document_shared: 1,
  first_user_message: 1,
  chat_message_sent: 1,
  chat_persona_changed: 1,
  first_agent_run_started: 1,
  first_agent_run_completed: 1,
  second_agent_run_completed: 1,
  third_agent_run_completed: 1,
  first_file_uploaded: 1,
  first_integration_connected: 1,
  first_automation_created: 1,
  tool_invoked: 1,
  tool_completed: 1,
  integration_connected: 1,
  integration_disconnected: 1,
  invite_sent: 1,
  invite_accepted: 1,
  file_uploaded: 1,
  day2_returned: 1,
  day7_returned: 1,
  weekly_active: 1,
  spend_limit_prompt_shown: 1,
  spend_limit_approved: 1,
  spend_limit_dismissed: 1,
  spend_limit_cap_raised_manual: 1,
  spend_limit_uncapped_prompt_shown: 1,
  spend_limit_uncapped_authorized: 1,
  spend_limit_uncapped_revoked: 1,
  upgrade_cta_viewed: 1,
  upgrade_cta_clicked: 1,
  checkout_started: 1,
  checkout_abandoned: 1,
  subscription_started: 1,
  subscription_changed: 1,
  subscription_canceled: 1,
  payment_failed: 1,
  ai_credits_purchased: 1,
  error_shown: 1,
  store_listing_viewed: 1,
  popup_shown: 1,
  popup_dismissed: 1,
  popup_feedback_submitted: 1,
  transcript_opened: 1,
  transcript_entity_clicked: 1,
  transcript_demo_seeded: 1,
  meeting_recording_started: 1,
  meeting_recording_completed: 1,
  meeting_summary_viewed: 1,
  meeting_action_items_created: 1,
  calendar_connected: 1,
  home_initial_chat_submitted: 1,
  home_recent_thread_opened: 1,
  home_quick_start_clicked: 1,
  home_dock_app_open: 1,
  home_root_create: 1,
  home_root_dock_open: 1,
  home_app_focus_open: 1,
  home_widget_added: 1,
  home_widget_layout_saved: 1,
  dashboard_opened: 1,
  dashboard_widget_added: 1,
  dashboard_widget_refreshed: 1,
  dashboard_widget_errored: 1,
  dashboard_widget_removed: 1,
  database_connection_created: 1,
  database_connection_tested: 1,
  database_query_run: 1,
  database_provider_selected: 1,
  database_provider_request_submitted: 1,
  browser_credential_prompt_shown: 1,
  browser_credential_filled: 1,
  browser_credential_saved: 1,
  browser_credential_dismissed: 1,
  browser_card_filled: 1,
  browser_identity_filled: 1,
  browser_totp_filled: 1,
  browser_profile_revoked_all: 1,
  mediagen_modal_opened: 1,
  mediagen_template_selected: 1,
  mediagen_generation_completed: 1,
  space_sidebar_loaded_ms: 1,
  space_chat_first_paint_ms: 1,
  integration_pill_shown: 1,
  integration_pill_clicked: 1,
  integration_pill_added: 1,
  integration_pill_details_clicked: 1,
  integration_pill_try_in_chat_clicked: 1,
  service_creator_menu_built: 1,
  service_creator_invoked: 1,
  service_creator_completed: 1,
  service_creator_failed: 1,
  service_creator_connect_clicked: 1,
  tool_analysis_completed: 1,
  tool_chip_toggled: 1,
  tool_sticky_action_clicked: 1,
  tool_specialty_selected: 1,
  mini_tool_viewed: 1,
  mini_tool_submitted: 1,
  community_category_tab_clicked: 1,
  doc_template_config_submitted: 1,
  doc_template_logo_generate_selected: 1,
  data_source_installed: 1,
  data_source_searched: 1,
  data_source_attached: 1,
  physician_tool_submitted: 1,
  physician_widget_opened: 1,
  physician_widget_computed: 1,
  wizard_started: 1,
  ws_health_issue: 1,
  web_vital_recorded: 1,
} satisfies Record<EventName, number>));

// ---------------------------------------------------------------------------
// User traits (sent via identify)
// ---------------------------------------------------------------------------
export interface UserTraits {
  // Identity
  email?: string;
  name?: string;
  anonymous_id?: string;
  // Plan / monetization
  plan?: string;
  personal_plan?: string;
  personal_org_plan?: string;
  primary_org_plan?: string;
  org_id?: string;
  org_role?: string;
  is_paid?: boolean;
  is_payg?: boolean;
  monetization_type?: 'free' | 'payg' | 'subscription' | 'mixed';
  card_on_file?: boolean;
  payg_activated_at?: string;
  // Signup / acquisition
  signup_at?: string;
  signup_method?: string;
  funnel_id?: string;
  persona?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  is_internal?: boolean;
  // Onboarding / activation
  onboarding_completed_at?: string;
  onboarded_via?: string;
  wizard_variant?: string;
  onboarding_goals_count?: number;
  onboarding_integrations_connected_count?: number;
  preferred_language?: string;
  first_value_at?: string;
  ttv_seconds?: number;
  second_value_at?: string;
  ttsr_seconds?: number;
  third_value_at?: string;
  time_to_third_value_seconds?: number;
  // Retention
  last_seen_at?: string;
  day2_returned?: boolean;
  day7_returned?: boolean;
  day30_returned?: boolean;
  day60_returned?: boolean;
  day90_returned?: boolean;
  monthly_active_at?: string;
  daily_active_at?: string;
  days_since_last_agent_run?: number;
  // PMF
  is_power_user?: boolean;
  feature_breadth_30d?: number;
  budget_utilization_pct?: number;
  churn_risk_threshold?: string;
  // Collaboration
  seats_invited?: number;
  // Tool / integration usage
  tools_used_count?: number;
  unique_tools_used?: string[];
  integrations_used?: string[];
  integrations_connected_count?: number;
  integrations_connected_list?: string[];
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Org traits (sent via group)
// ---------------------------------------------------------------------------
export interface OrgTraits {
  plan?: string;
  seat_count?: number;
  seats_invited?: number;
  seats_active?: number;
  mrr?: number;
  mrr_total?: number;
  mrr_subscription?: number;
  payg_active?: boolean;
  payg_revenue_trailing_30d?: number;
  team_activated?: boolean;
  created_at?: string;
  org_integrations_connected?: string[];
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Database connections
// ---------------------------------------------------------------------------
export interface DatabaseConnectionCreatedProps {
  driver: 'postgres' | 'mongodb' | string;
  connection_id: string;
  test_passed: boolean;
}

export interface DatabaseConnectionTestedProps {
  driver: 'postgres' | 'mongodb' | string;
  connection_id: string;
  success: boolean;
}

export interface DatabaseQueryRunProps {
  driver: 'postgres' | 'mongodb' | string;
  connection_id: string;
  row_count: number;
  truncated: boolean;
}

export interface FirstDatabaseConnectionCreatedProps {
  driver: 'postgres' | 'mongodb' | string;
  connection_id: string;
}

export interface DatabaseProviderSelectedProps {
  provider: string;
}

export interface DatabaseProviderRequestSubmittedProps {
  provider: string;
  form_name: string;
}

// ---------------------------------------------------------------------------
// Browser credential + profile analytics (Phases 1–3)
// ---------------------------------------------------------------------------
export interface BrowserCredentialPromptShownProps {
  mode: 'pick' | 'empty' | 'save';
  domain: string;
  hasMatches: boolean;
}

export interface BrowserCredentialFilledProps {
  credentialId: string;
  domain: string;
}

export interface BrowserCredentialSavedProps {
  credentialId: string;
  domain: string;
  source: 'save_prompt' | 'agent';
}

export interface BrowserCredentialDismissedProps {
  reason: string;
}

export interface BrowserCardFilledProps {
  domain: string;
}

export interface BrowserIdentityFilledProps {
  domain: string;
}

export interface BrowserTotpFilledProps {
  domain: string;
}

export interface BrowserProfileHydratedProps {
  domainCount: number;
  dataspaceKind: 'personal' | 'org';
}

export interface BrowserProfileSnapshotPersistedProps {
  domain: string;
  cookiesCount: number;
}

export type BrowserProfileRevokedAllProps = Record<string, never>;

export interface BrowserProfileDataspaceSwitchedProps {
  fromKind: 'personal' | 'org';
  toKind: 'personal' | 'org';
}

export interface BrowserAgentActionInvokedProps {
  action: string;
  domain: string;
}

export interface BrowserAgentActionFailedProps {
  action: string;
  domain: string;
  error: string;
}

// ---------------------------------------------------------------------------
// MediaGen
// ---------------------------------------------------------------------------
export interface MediagenModalOpenedProps {
  source: 'topbar' | 'agent' | 'empty_canvas_hero' | string;
}

export interface MediagenTemplateSelectedProps {
  templateId: string;
  category: string | null;
  mode: 'window' | 'modal' | string;
}

export type MediagenGenerationCompletedProps =
  | {
      ms: number;
      count: number;
      target: 'modal_handoff' | 'window_results' | string;
      templateId?: string;
      seedId?: string;
    }
  | {
      /** Distinguishes the ImageStudio variant from the MediaGen modal variant above. */
      tool: 'image_studio';
      mode: string;
      duration_ms: number;
      variant_count: number;
      generation_mode: string;
    };

// ---------------------------------------------------------------------------
// Performance — space sidebar / chat first paint
// ---------------------------------------------------------------------------

export interface SpaceSidebarLoadedMsProps {
  /** Space ID being loaded. */
  space_id: string;
  /** Org context the space belongs to. */
  org_id?: string | null;
  /**
   * Wall-clock milliseconds from the start of `spaceEntityStore.initializeSpace`
   * to when Stage 1 entities were painted into the cache. This is the
   * user-perceived sidebar latency.
   */
  ms_to_stage1_paint: number;
  /**
   * Wall-clock milliseconds from the start of `initializeSpace` to when the
   * full Stage 1 + Stage 2 result was committed (cross-space refs included).
   * Equal to ms_to_stage1_paint when there are no edges to BFS.
   */
  ms_to_complete: number;
  /** Number of entities painted at Stage 1. */
  stage1_entity_count: number;
  /** Number of cross-space ref entities added in Stage 2. */
  stage2_entity_count: number;
  /** Source of the cache hit ('idb', 'rest', or 'cold'). */
  source: 'idb' | 'rest' | 'cold';
}

export interface SpaceChatFirstPaintMsProps {
  /** Thread ID being initialized. */
  thread_id: string;
  /**
   * Wall-clock milliseconds from the start of
   * `chatStore.initializeFromThread` to when conversationStore was seeded
   * with the bootstrap response (or the legacy fetchMessages fallback).
   */
  ms_to_first_paint: number;
  /** Number of messages painted on first paint. */
  message_count: number;
  /** Whether the thread has an active agent run (queued/running). */
  has_active_run: boolean;
  /** Which load path ran ('bootstrap' | 'legacy'). */
  source: 'bootstrap' | 'legacy';
}

// ---------------------------------------------------------------------------
// Integration pill (chat input smart-suggest feature)
// ---------------------------------------------------------------------------
export interface IntegrationPillShownProps {
  slug: string;
  source: string;
}

export interface IntegrationPillClickedProps {
  slug: string;
}

export interface IntegrationPillAddedProps {
  slug: string;
  scope: 'self' | 'org';
}

export interface IntegrationPillDetailsClickedProps {
  slug: string;
}

export interface IntegrationPillTryInChatClickedProps {
  slug: string;
}

export interface IntegrationPillOrgAddClickedProps {
  slug: string;
}

// ---------------------------------------------------------------------------
// Service Creator (CreateMenu "Create from…" section)
// ---------------------------------------------------------------------------
export interface ServiceCreatorMenuBuiltProps {
  totalCreators: number;
  totalVariants: number;
  connectedCount: number;
  source: 'sidebar_header' | 'group_plus' | 'dataspace_header';
}

export interface ServiceCreatorInvokedProps {
  variantId: string;
  slug: string;
  method: string;
  hasNativeConnection: boolean | null | undefined;
  hasComposio: boolean | null | undefined;
  hasCredential: boolean;
}

export interface ServiceCreatorCompletedProps {
  variantId: string;
  slug: string;
  method: string;
  entityType: string;
  durationMs: number;
}

export interface ServiceCreatorFailedProps {
  variantId: string;
  slug: string;
  method: string;
  errorCode: string | number;
  durationMs: number;
}

export interface ServiceCreatorConnectClickedProps {
  slug: string;
  source: 'create_menu_pill';
}

// ---------------------------------------------------------------------------
// Tool intake + TOC flow
// ---------------------------------------------------------------------------
export interface ToolIntakeShownProps {
  slug: string;
  mode_list: string[];
}

export interface ToolIntakeSubmittedProps {
  slug: string;
  mode: 'paste' | 'upload' | 'gdocs';
  has_text: boolean;
  file_count: number;
  has_gdocs_url: boolean;
}

export interface ToolChipToggledProps {
  slug: string;
  chip_id: string;
  on: boolean;
  recommended: boolean;
}

export interface ToolStickyActionClickedProps {
  slug: string;
  action: string;
}

export interface ToolAnalysisCompletedProps {
  slug: string;
  source_kind: 'paste' | 'entity';
  headings_found: number;
}

export interface ToolSpecialtySelectedProps {
  slug: string;
  specialty: string;
  tool_kind: string;
}

// ---------------------------------------------------------------------------
// Marketing mini-tools (JSON-config workflow tools at /tools/:slug)
// Distinct from the AI tool-runtime (tool_invoked/tool_completed) and the
// TOC split-hero intake flow (tool_intake_*).
// ---------------------------------------------------------------------------
export interface MiniToolViewedProps {
  slug: string;
  funnel_id?: string;
  input_type: string;
  persona?: string;
}

export interface MiniToolSubmittedProps {
  slug: string;
  funnel_id?: string;
  input_type: string;
  has_input: boolean;
  input_length: number;
  authenticated: boolean;
}

// ---------------------------------------------------------------------------
// Community hub events
// ---------------------------------------------------------------------------
export interface CommunityCategoryTabClickedProps {
  category_id: string | null;
  category_name: string | null;
}

export interface CommunityArtifactFilterChangedProps {
  artifact_kind: string | null;
}

export interface DocTemplateConfigSubmittedProps {
  template_id: string;
  has_company_name: boolean;
  has_details: boolean;
  logo_mode: 'none' | 'upload' | 'generate';
  has_brand_color: boolean;
  has_logo_prompt: boolean;
}

export interface DocTemplateLogoGenerateSelectedProps {
  template_id: string;
  company_name_provided: boolean;
}

// ---------------------------------------------------------------------------
// Page view props
// ---------------------------------------------------------------------------
export interface PageProps {
  page_title?: string;
  [key: string]: unknown;
}
